import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "@/lib/LangContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-b border-white/10"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono-x text-xs tracking-[0.3em] text-brand-500 font-bold">
            {t.contact.overline}
          </p>
          <h2 className="mt-4 font-display font-medium tracking-tighter text-white text-4xl sm:text-5xl">
            {t.contact.title}
          </h2>
          <p className="mt-6 font-mono-x text-sm text-slate-300 leading-relaxed">
            {t.contact.description}
          </p>
          <div className="mt-10 space-y-4 font-mono-x text-sm text-slate-400">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500">
                Email
              </div>
              <div className="text-white">contacto@rio-code.com</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500">
                Response Time
              </div>
              <div className="text-white">&lt; 24h</div>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          onSubmit={submit}
          className="lg:col-span-7 border border-white/10 bg-slate-950/40 backdrop-blur-sm p-8 lg:p-10 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              testid="contact-name"
              name="name"
              label={t.contact.name}
              value={form.name}
              onChange={onChange}
              required
            />
            <Field
              testid="contact-email"
              name="email"
              type="email"
              label={t.contact.email}
              value={form.email}
              onChange={onChange}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              testid="contact-company"
              name="company"
              label={t.contact.company}
              value={form.company}
              onChange={onChange}
            />
            <div className="flex flex-col">
              <label className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                {t.contact.service}
              </label>
              <select
                data-testid="contact-service"
                name="service"
                value={form.service}
                onChange={onChange}
                className="bg-black border border-white/10 focus:border-brand-500 focus:outline-none text-white px-3 py-3 font-mono-x text-sm transition-colors duration-200"
              >
                <option value="">—</option>
                {t.contact.service_options.map((o, i) => (
                  <option key={i} value={o} className="bg-black">
                    {o}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col">
            <label className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
              {t.contact.message}
            </label>
            <textarea
              data-testid="contact-message"
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={5}
              className="bg-black border border-white/10 focus:border-brand-500 focus:outline-none text-white px-3 py-3 font-mono-x text-sm resize-none transition-colors duration-200"
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="group inline-flex items-center gap-2 bg-brand-500 text-black px-6 py-3 font-mono-x text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t.contact.submitting : t.contact.submit}
            <PaperPlaneRight
              size={14}
              weight="fill"
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </button>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ testid, label, name, type = "text", value, onChange, required }) => (
  <div className="flex flex-col">
    <label className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
      {label}
    </label>
    <input
      data-testid={testid}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="bg-black border border-white/10 focus:border-brand-500 focus:outline-none text-white px-3 py-3 font-mono-x text-sm transition-colors duration-200"
    />
  </div>
);
